/**
 * Beautify JSON substrings found in a larger text block.
 * This scans for JSON objects by finding balanced braces while respecting
 * quoted strings and escape characters, attempts to parse each JSON block,
 * and replaces it with a pretty-printed JSON string (2 spaces).
 *
 * @param text - The text containing potential JSON blocks
 * @returns The text with JSON blocks beautified
 */
export const beautifyJsonStringsIn = (text: string): string => {
  if (!text) return text;

  const parts: Array<{ type: "text" | "json"; content: string }> = [];
  let i = 0;
  const len = text.length;

  while (i < len) {
    // Find next '{' that might start a JSON object
    const braceIndex = text.indexOf("{", i);
    if (braceIndex === -1) {
      // No more braces, push remaining text
      parts.push({ type: "text", content: text.slice(i) });
      break;
    }

    // Push any text before the brace
    if (braceIndex > i) {
      parts.push({ type: "text", content: text.slice(i, braceIndex) });
    }

    // Attempt to find the matching closing brace taking strings and escapes into account
    let depth = 0;
    let inString = false;
    let escape = false;
    let j = braceIndex;
    let found = false;

    for (; j < len; j++) {
      const ch = text[j];

      if (escape) {
        escape = false;
        continue;
      }

      if (ch === "\\") {
        escape = true;
        continue;
      }

      if (ch === '"') {
        inString = !inString;
        continue;
      }

      if (!inString) {
        if (ch === "{") {
          depth++;
        } else if (ch === "}") {
          depth--;
          if (depth === 0) {
            // include this closing brace
            j = j + 1; // slice end is exclusive
            found = true;
            break;
          }
        }
      }
    }

    if (!found) {
      // Could not find a balanced object - treat brace as normal text
      parts.push({ type: "text", content: "{" });
      i = braceIndex + 1;
      continue;
    }

    const candidate = text.slice(braceIndex, j);
    try {
      const parsed = JSON.parse(candidate);
      const pretty = JSON.stringify(parsed, null, 2);
      // Ensure surrounding newlines for readability
      parts.push({ type: "json", content: pretty });
    } catch (e) {
      // Not valid JSON - treat as plain text
      parts.push({ type: "text", content: candidate });
    }

    i = j;
  }

  // Join parts into a single string; json parts are already pretty strings
  return parts.map(p => p.content).join("");
};
